import { CloseRounded } from "@mui/icons-material";
import { Box, Chip, IconButton, Skeleton, Stack, Tab, Tabs, Typography, Link } from "@mui/material";
import React, { useEffect } from "react";
import { ExternalAccessDto } from "../../../Core/Api/Types/access-models";
import { ObjectType } from "../../../Core/Api/Types/object-type";
import { AssetTypeAlphaColor, GetFormattedDateTime } from "../../../Utils/helpers";
import { ExternalAccessExpiresOnDate } from "../../Access/ExternalAccess/external-access-expires-in";
import ExternalAccessPermissionSummary from "../../Access/ExternalAccess/external-access-permission-summary";
import { ExternalAccessStatusDetailsChip } from "../../Access/ExternalAccess/external-access-status";
import AssetThumbnail from "../../Misc/AssetThumbnail";
import Panel from "../Panel/panel";
import { assetService } from "../../../Core/Api/services";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import { DbAssetObjectInfos } from "../../../Core/Api/Types/db-asset-object-infos";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { Link as RouterLink } from "react-router";
import { routePaths } from "../../../Router/router";
import { useTranslation } from "react-i18next";
import ResourceNotification from "../../Notification/resource-notification";
import { ActivityKind } from "../../../Core/Api/Types/activity-kind";

export interface CustomExternalAccessDto extends ExternalAccessDto {
  userName: string;
}

interface AccessPanelProps {
  open: boolean;
  accessDto: CustomExternalAccessDto;
  closePanel: () => void;
}

const ExternalAccessPanel = (props: AccessPanelProps) => {
  const { t } = useTranslation();
  const { open, accessDto, closePanel } = props;

  const enqueueSnackbar = useCustomSnackbar();

  const [activeTab, setActiveTab] = React.useState<"settings" | "items" | "activities">("settings");
  const [children, setChildren] = React.useState<DbAssetObjectInfos[]>([]);
  const [childrenLoading, setChildrenLoading] = React.useState(true);

  const objectType = React.useMemo(() => accessDto.objectType || ObjectType.Unknown, [accessDto.objectType]);
  const itemsCount = React.useMemo(() => accessDto.objectChildCount || 1, [accessDto.objectChildCount, objectType]);

  React.useEffect(() => {
    assetService
      .GetChildrenByObjectId(accessDto.objectId, GlobalConst.MAX_CHILDREN)
      .then((res) => {
        if (!res.success) {
          enqueueSnackbar(res.message || t("alert:error.oopsSomethingWentWrongWithoutDot"), "error");
        } else {
          setChildren(res.children || []);
        }
      })
      .finally(() => setChildrenLoading(false));
  }, [accessDto.objectId]);

  return (
    <Panel
      open={open}
      noWrapper
      reload={accessDto.id}
      header={
        <Stack
          direction="row"
          alignItems={"center"}
          py={1.5}
          pl={1}
          pr={2}
          width="100%"
          height="48px"
          sx={{ borderBottom: "1px solid", borderColor: "divider" }}
        >
          <Stack direction="row" flexGrow={1} sx={{ overflow: "hidden", mr: 2, alignItems: "center" }}>
            <IconButton size="small" aria-label="close" onClick={closePanel}>
              <CloseRounded />
            </IconButton>
            <Typography variant="h5" fontWeight={500} color="text.primary">
              {t("component:body.accessDetails")}
            </Typography>
          </Stack>
        </Stack>
      }
      onClose={closePanel}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        p={2}
        sx={{
          borderBottom: "1px solid",
          borderColor: "divider",
          background: `linear-gradient(225.52deg, ${AssetTypeAlphaColor(objectType, 0.12)} 0%, ${AssetTypeAlphaColor(
            objectType,
            0.02
          )} 50.71%, ${AssetTypeAlphaColor(objectType, 0.08)} 100%)`,
        }}
      >
        <Stack justifyContent="space-between" py={0.5}>
          <Stack spacing={0.5}>
            <Typography variant="h6" color="text.tier">
              {t("component:body.accessWithId", { id: accessDto.id })}
            </Typography>
            <Typography variant="h3">{accessDto.objectName}</Typography>
          </Stack>

          <ExternalAccessPermissionSummary permissions={accessDto.permissions} />
        </Stack>
        <AssetThumbnail type={objectType} size="large" />
      </Stack>
      <Tabs
        value={activeTab}
        onChange={(e, value) => setActiveTab(value)}
        variant="fullWidth"
        sx={(theme) => ({
          flexShrink: 0,
          position: "sticky",
          top: 0,
          background: theme.palette.background.default,
          zIndex: 2,
        })}
      >
        <Tab label={t("common:settings")} value="settings" />
        <Tab label={t("page:tab.label.itemsCount", { count: itemsCount })} value="items" />
        <Tab label={t("common:activities")} value="activities" />
      </Tabs>
      <Box p={2}>
        {activeTab === "settings" && (
          <Stack spacing={1}>
            <SettingsListItem label={t("table:headerName.sharedTo")}>
              <Chip label={accessDto.email} />
            </SettingsListItem>
            <SettingsListItem label={t("common:status")}>
              <ExternalAccessStatusDetailsChip status={accessDto.status} />
            </SettingsListItem>
            <SettingsListItem label={t("table:headerName.expiresIn")}>
              <ExternalAccessExpiresOnDate dateString={accessDto.expirationDate} status={accessDto.status} />
            </SettingsListItem>
            <SettingsListItem label={t("common:createdAtLowerCase")}>
              <Typography variant="body1">{GetFormattedDateTime(accessDto.createdAt)}</Typography>
            </SettingsListItem>
            <SettingsListItem label={t("common:createdBy")}>
              <Chip label={accessDto.userName} color="accent" />
            </SettingsListItem>
          </Stack>
        )}
        {activeTab === "items" && (
          <Stack spacing={2}>
            {!childrenLoading ? (
              <>
                {children.map((child) => (
                  <Stack key={child.id} direction="row" alignItems="center" spacing={1}>
                    <AssetThumbnail type={child.type} size="micro" />
                    <Link
                      underline="hover"
                      variant="body1"
                      component={RouterLink}
                      color="text.primary"
                      to={`${routePaths.drive.path}/${child.id}`}
                    >
                      {child.name}
                    </Link>
                    {child.childCount && child.childCount > 0 && (
                      <Chip label={`+${child.childCount}`} color="primary" variant="outlined" size="small" />
                    )}
                  </Stack>
                ))}
              </>
            ) : (
              <>
                <Skeleton variant="rounded" height={20} />
                <Skeleton variant="rounded" height={20} />
                <Skeleton variant="rounded" height={20} />
              </>
            )}
          </Stack>
        )}
        {activeTab === "activities" && (
          <Stack spacing={1}>
            <ResourceNotification resourceId={accessDto.id} resourceKind={ActivityKind.ExternalAccess} />
          </Stack>
        )}
      </Box>
    </Panel>
  );
};

export default ExternalAccessPanel;

const SettingsListItem = (props: { label: string; children: React.ReactNode }) => {
  const { label, children } = props;
  return (
    <Stack direction="row" justifyContent="space-between">
      <Typography variant="body1" color="text.secondary">
        {label}
      </Typography>
      {children}
    </Stack>
  );
};
